import {type AppDispatch, type RootState} from "../store";
import Configuration from "../../Configuration";
import {authReducerActions} from "../reducer/authReducer.ts";
import {type AuthProviderProps} from "react-oidc-context";
import {type UserProfile} from "oidc-client-ts";
import {type UserScope} from "../model/user/UserScope.ts";
import userService from "./userService.ts";
import {loadSupportContext, saveSupportContext} from "../utils/supportContextStorage.ts";
import {RightsMapper} from "../components/rights/RightsMapper.ts";

// A reload keeps the support context of the tab, but only for users still allowed to use it.
const restoreSupportContext = (dispatch: AppDispatch, getState: () => RootState, profile: UserProfile) => {
    const stored = loadSupportContext();
    if (!stored || getState().auth.supportContext) return;
    const authorities = (profile.authorities as string[] | undefined) ?? [];
    if (RightsMapper.getUserPermissions(authorities).includes("SupportContext_write")) {
        dispatch(authReducerActions.startSupportContext(stored));
    } else {
        saveSupportContext(null);
    }
};

class AuthService {
    loginSuccess = (profile: UserProfile, accessToken: string, userScope: UserScope) => {
        return (dispatch: AppDispatch, getState: () => RootState) => {
            dispatch(authReducerActions.loginSuccess({
                profile,
                accessToken,
                userScope
            }));
            restoreSupportContext(dispatch, getState, profile);
            if (!getState().auth.myAccount) {
                void userService.getMyAccount()
                    .then(response => {
                        dispatch(authReducerActions.setAccount(response.myAccount));
                        dispatch(authReducerActions.setAgreementFaulty(response.agreementFaulty));
                    });
                // M7: removed dispatch(pricingReducerActions.getUserDetails(profile.sub))
                // — `pricing.username` had no readers besides ContractPage,
                // which now reads `state.auth.profile.sub` directly.
            }
        };
    };
}

const requiredScopes = "openid profile COCKPIT_CLIENT";
export const oidcConfig: AuthProviderProps = {
    authority: Configuration.value("authServer"),
    client_id: Configuration.value("clientId"),
    client_secret: Configuration.value("clientSecret"),
    redirect_uri: window.location.origin,
    response_type: "code",
    scope: requiredScopes,
    popup_redirect_uri: window.location.origin,
    automaticSilentRenew: false,
    loadUserInfo: true,
    post_logout_redirect_uri: window.location.origin,
    // Safety net for the hidden-iframe signin: the login page forbids framing
    // (X-Frame-Options deny), so a stray iframe attempt would otherwise hang for
    // the 10 s library default before failing (#55620).
    silentRequestTimeoutInSeconds: 5,
};

const authService = new AuthService();
export default authService;
